import {EventEmitter, Injectable} from '@angular/core';
import {IPlatformToolHandler} from '@shared/components/platform-tool/platform-tool-container/platform-tool-component';
import {InvalidateSubject} from '@shared/helpers/invalidate.subject';
import {MagicLinkModel, TabModel} from '@shared/models/platform-tool-models';
import {PlatformCommand} from '@shared/commands/command-base';
import {CommandService} from '@core/services/command.service';
import {take} from 'rxjs/operators';
import {FuseSplashScreenService} from '@barebone-fuse-services/splash-screen.service';
import {SnackBarService} from '@core/services/snack-bar.service';
import {Router} from '@angular/router';
import {MatDialog} from '@angular/material/dialog';
import {AddUserComponent} from '@features/admin-panel/components/add-user/add-user.component';
import {DialogService} from '@shared/services/dialog-service';

@Injectable({
  providedIn: 'root',
})
export class AdminPanelCompanyDetailHandler implements IPlatformToolHandler {
  invalidateSubject: InvalidateSubject;
  magicLinkModel: MagicLinkModel;
  selectedTabModel: TabModel;
  magicLinkModelValueChanged: EventEmitter<any> = new EventEmitter<any>();

  constructor(
    private dialog: MatDialog,
    private router: Router,
    private commandService: CommandService,
    private snackBarService: SnackBarService,
    private fuseSplashScreenService: FuseSplashScreenService,
    private dialogService: DialogService
  ) {}

  destroy(): void {
    this.invalidateSubject.close();
  }

  onBeforeLoad(): void {}

  executeCommand(command: PlatformCommand) {
    if (command.name === 'AddUserPlatformCommand') {
      this.addUser(command);
    } else {
      this.executeCommandToApi(command);
    }
  }

  private addUser(command: PlatformCommand) {
    const companyId = command.fieldValues['CompanyId'] ?? this.getCompanyId();
    const dialogRef = this.dialog.open(AddUserComponent, {
      width: '600px',
      disableClose: true,
      data: {company: {companyId}}
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result?.save) {
        const user = result.user;
        command.fieldValues['User'] = {
          firstName: user.firstName,
          lastName: user.lastName,
          email: user.email,
          companyId: user.companyId ?? companyId,
          companyName: user.companyName,
          designation: user.designation,
          userName: ''
        };
        command.waitForResponse = true;
        this.executeCommandToApi(command);
      }
    });
  }

  executeCommandToApi(command: PlatformCommand) {
    this.fuseSplashScreenService.show();
    this.commandService.executePlatformCommand(command).pipe(take(1)).subscribe(
      commandResponse => {
        this.fuseSplashScreenService.hide();
        const fv = commandResponse?.fieldValues;
        if (fv?.['SnackBarMessage']) {
          this.snackBarService.showInformation(fv['SnackBarMessage']);
        }
        if (fv?.['ShowWarning']) {
          this.snackBarService.showWarning(fv['ShowWarning']);
        }
        if (fv?.['ShowErrorMessage']) {
          this.snackBarService.showError(fv['ShowErrorMessage']);
        }
        if (fv?.['RedirectUrl']) {
          this.router.navigateByUrl(fv['RedirectUrl']).then();
          return;
        }
        this.invalidateSubject?.invalidateAlways('Refresh');
      },
      error => {
        this.fuseSplashScreenService.hide();
        console.error('Failed ' + command.name, error);
      }
    );
  }

  setSelectedTab(selectedTab: TabModel): void {
    this.selectedTabModel = selectedTab;
  }

  valueChanged($event: any) {
    // Mirrors FilingFormsHandler.valueChanged: the cell viewer's edit pencil
    // raises action.id='Edit'. We open a cell-editor dialog wrapping the
    // FieldDefinition's configured CellEditor (e.g. NumericCellEditor) and,
    // on OK, dispatch the save command registered via AddAdditionalCommand
    // under the field key.
    if ($event?.action?.id === 'Edit') {
      if ($event.fieldDefinition?.metaData?.ExecuteCommandOnEdit !== 'true') {
        this.openCellEditor($event);
        return;
      }
    }

    if (!$event?.fieldDefinition) {
      return;
    }

    const additional = this.magicLinkModel?.additionalCommands ?? {};
    const command = additional[$event.action?.id] ?? additional[$event.fieldDefinition.key];
    if (!command) {
      return;
    }
    command.fieldValues['ItemId'] = $event.item?.id;
    command.fieldValues['FieldKey'] = $event.fieldDefinition.key;
    command.fieldValues['FieldValue'] = $event.item?.cells?.[$event.fieldDefinition.key]?.value;
    this.executeCommand(command);
  }

  private openCellEditor($event: any) {
    const sourceType = this.magicLinkModel?.tabBar?.tabs?.[0]?.tabData?.['SourceType'];
    const minWidth = '400px';
    const maxWidth = '1500px';
    const itemCopy = JSON.parse(JSON.stringify($event.item));

    this.dialogService.showCellEditorDialog(itemCopy, $event.fieldDefinition, minWidth, maxWidth, sourceType)
      .subscribe(state => {
        if (state?.action !== 'OK') {
          return;
        }
        const fieldKey = $event.fieldDefinition.key;
        $event.item.cells[fieldKey] = itemCopy.cells[fieldKey];

        const additional = this.magicLinkModel?.additionalCommands ?? {};
        const command = additional[fieldKey];
        if (!command) {
          return;
        }
        command.fieldValues['ItemId'] = $event.item.id;
        command.fieldValues['FieldKey'] = fieldKey;
        command.fieldValues['FieldValue'] = itemCopy.cells?.[fieldKey]?.value;
        this.executeCommand(command);
      });
  }

  private getCompanyId(): string {
    return this.magicLinkModel?.tabBar?.tabs
      ?.find(t => t.tabData?.['CompanyId'])
      ?.tabData?.['CompanyId'];
  }
}
